import {
  Component,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Modal } from '../../shared/components/modal/modal';
import { CreatesByBusiness } from '../../shared/common/CreatesByBusiness.class';
import { ClientInput, Professional, Service } from '../../shared/interfaces';
import { PageTitleService } from '../../shared/services';
import { PeopleAdd } from '../people/people-add/people-add';
import { formatDate, registerLocaleData, UpperCasePipe } from '@angular/common';
import { ConfirmModal } from '../../shared/components/confirm-modal/confirm-modal';
import { debounceTime, distinctUntilChanged, Subject, tap, forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, catchError } from 'rxjs/operators';
import { MembershipService } from '../../services/membership/membership.service';
import { ProfessionalPaymentService } from '../../services/membership/professional-payment.service';
import { AuthService } from '../../services/auth';
import { HttpClient, HttpContext } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';
import esCo from '@angular/common/locales/es-CO';
registerLocaleData(esCo);

@Component({
  selector: 'app-staff',
  imports: [Modal, ConfirmModal, UpperCasePipe, RouterLink],
  templateUrl: './staff.html',
  styleUrl: './staff.css',
  providers: [{ provide: LOCALE_ID, useValue: 'es-CO' }],
})
export class Staff
  extends CreatesByBusiness<Professional | any, ClientInput>
  implements OnInit
{
  clients: Professional[] = [];
  filteredProfessionals: Professional[] = [];
  loading = false;
  page = 1;
  pageSize = 20;
  totalCount = 0;
  searchTerm = '';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';
  totalPages = 1;
  titleService = inject(PageTitleService);
  PeopleAddForm = PeopleAdd;
  protected endpoint = 'professionals';
  confirmOpen = false;
  toDeleteId: string | null = null;
  toDeleteName: string | null = null;
  confirmToggleOpen = false;
  toToggleProfessional: Professional | null = null;
  modalTitle = 'Nuevo profesional';
  modalInputs: Record<string, unknown> = {};
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<string>();
  private membershipService = inject(MembershipService);
  private professionalPaymentService = inject(ProfessionalPaymentService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private apiBaseUrl = environment.apiUrl;

  // Professional limit info
  paidProfessionalsCount = 0;
  activeProfessionalsCount = 0;
  canCreateMore = true;

  // Services per professional
  professionalServicesMap = signal<Record<string, Service[]>>({});
  skeletonRows = Array(6).fill(0);

  // Pagination helper
  get paginationArray(): number[] {
    return Array(this.totalPages).fill(0);
  }

  readonly statusFilters: { value: 'all' | 'active' | 'inactive'; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'active', label: 'Activos' },
    { value: 'inactive', label: 'Inactivos' },
  ];

  /** Business-wide totals per status filter (not just the current page) */
  statusCount(filter: 'all' | 'active' | 'inactive'): number {
    if (filter === 'all') return this.totalCount;
    if (filter === 'active') return this.activeProfessionalsCount;
    return Math.max(0, this.totalCount - this.activeProfessionalsCount);
  }

  /** Plan seats are only shown to business owners with a known paid limit */
  get showSeats(): boolean {
    return !this.authService.isSystemAdmin() && this.paidProfessionalsCount > 0;
  }

  /** One entry per paid seat; taken seats are named after an active professional on this page when known */
  get seats(): { taken: boolean; name: string }[] {
    const seatCount = Math.max(this.paidProfessionalsCount, this.activeProfessionalsCount);
    const active = this.clients.filter((p) => p.isActive);
    return Array.from({ length: seatCount }, (_, i) => {
      const taken = i < this.activeProfessionalsCount;
      const prof = taken ? active[i] : undefined;
      return { taken, name: prof ? this.fullName(prof) : '' };
    });
  }

  get freeSeats(): number {
    return Math.max(0, this.paidProfessionalsCount - this.activeProfessionalsCount);
  }

  /** Owner and admin can give a professional their own user from Settings > Users */
  get canInviteUsers(): boolean {
    return this.authService.has('users.invite');
  }

  fullName(p: Professional): string {
    return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
  }

  serviceNames(services: Service[]): string {
    return services.map((s) => s.name).join(', ');
  }

  /** When true, open add/pay flow once load completes (e.g. from grace period "Pagar ahora") */
  private openAddOnLoad = false;
  /** Number of unpaid professionals to pay for (from grace period banner) */
  private payUnpaidCount = 0;

  constructor() {
    super();
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((q) => {
          this.searchTerm = q;
          this.page = 1;
          this.load();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onSearch = (q: string) => this.search$.next(q);

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Profesionales',
      subtitle: 'Tu equipo, sus servicios y los cupos de tu plan',
      // Also reachable from the main menu; only show Configuración as parent when opened from there
      breadcrumbs: this.router.url.startsWith('/settings') ? [{ name: 'Configuración', url: '/settings' }] : [],
    });
    this.openAddOnLoad = this.route.snapshot.queryParams['add'] === '1';
    this.payUnpaidCount = parseInt(this.route.snapshot.queryParams['payUnpaid'] || '0', 10);
    this.load();
    this.loadProfessionalLimit();
  }

  /**
   * Load professional limit information
   */
  private loadProfessionalLimit(): void {
    // Skip for system administrators
    if (this.authService.isSystemAdmin()) {
      this.canCreateMore = true;
      return;
    }

    this.membershipService.getPaidProfessionalsCount().subscribe({
      next: (paidCount) => {
        this.paidProfessionalsCount = paidCount;
        // Inactive professionals don't take a seat, same rule as onCreateClient
        this.canCreateMore = this.activeProfessionalsCount < paidCount;
      },
      error: (error) => {
        console.error('Error loading professional limit:', error);
        // On error, allow creation (fail open)
        this.canCreateMore = true;
      },
    });
  }

  onStatusFilterChange(filter: string) {
    this.statusFilter = filter as 'all' | 'active' | 'inactive';
    this.applyStatusFilter();
  }

  private applyStatusFilter() {
    if (this.statusFilter === 'all') {
      this.filteredProfessionals = this.clients;
    } else if (this.statusFilter === 'active') {
      this.filteredProfessionals = this.clients.filter(p => p.isActive);
    } else {
      this.filteredProfessionals = this.clients.filter(p => !p.isActive);
    }
  }

  formatDate(dateStr: string): string {
    return formatDate(dateStr, 'MMM yyyy', 'es-CO');
  }

  onPageChange = (p: number) => {
    this.page = p;
    this.load();
  };

  load() {
    this.loading = true;
    this.preloader.show();
    this.businessService
      .getRecordsByBusinessId<Professional>(
        this.businessId,
        {
          page: this.page,
          pageSize: this.pageSize,
          searchTerm: this.searchTerm,
          sort: 'createdAt',
          order: 'desc',
        },
        this.endpoint
      )
      .subscribe({
        next: (res: any) => {
          this.clients = res.professionals ?? [];
          this.page = res.page;
          this.pageSize = res.pageSize;
          this.totalCount = res.totalCount;
          this.totalPages = res.totalPages;

          // Get total active professionals count from backend
          this.activeProfessionalsCount = res.activeCount ?? 0;

          this.loading = false;
          this.preloader.hide();

          // Apply status filter
          this.applyStatusFilter();

          // Update canCreateMore after loading
          if (!this.authService.isSystemAdmin()) {
            this.canCreateMore = this.activeProfessionalsCount < this.paidProfessionalsCount;
          }

          // Load services for professionals
          this.loadServicesForProfessionals(this.clients);

          // If navigated with payUnpaid=N (from grace period banner), trigger payment for N professionals
          if (this.payUnpaidCount > 0) {
            const count = this.payUnpaidCount;
            this.payUnpaidCount = 0;
            this.router.navigate([], { queryParams: {}, replaceUrl: true });
            this.membershipService.getMembershipStatus().subscribe((status) => {
              if (status.status === 'trial') {
                this.toastService.show(
                  'Durante la prueba no puedes comprar profesionales adicionales. Contrata un plan primero.',
                  'info',
                  6000
                );
                this.router.navigate(['/settings/billing'], { queryParams: { view: 'plans' } });
                return;
              }

              this.purchaseProfessionals(
                count,
                `Completa el pago para regularizar ${count} profesional(es) adicional(es)`
              );
            });
          }
          // If navigated with add=1, trigger same flow as "Crear Profesional"
          else if (this.openAddOnLoad) {
            this.openAddOnLoad = false;
            this.router.navigate([], { queryParams: {}, replaceUrl: true });
            this.onCreateClient();
          }
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.preloader.hide();
        },
      });
  }

  /**
   * Load services associated with each professional by fetching all services
   * and their professional assignments, then inverting the map.
   */
  private loadServicesForProfessionals(professionals: Professional[]) {
    if (professionals.length === 0) {
      this.professionalServicesMap.set({});
      return;
    }

    // Fetch all services for the business
    this.businessService
      .getRecordsByBusinessId<Service>(
        this.businessId,
        { page: 1, pageSize: 100, isActive: true },
        'services'
      )
      .pipe(
        switchMap((res: any) => {
          const services: Service[] = res.services ?? [];
          if (services.length === 0) return of({});

          // For each service, get its professionals
          const requests = services.map((s) =>
            this.http
              .get<Professional[]>(
                `${this.apiBaseUrl}/api/v1/businesses/${this.businessId}/services/${s.serviceId}/professionals`,
                { context: new HttpContext().set(REQUIRE_AUTH, true) }
              )
              .pipe(
                catchError(() => of([] as Professional[])),
                switchMap((profs) => of({ service: s, professionals: profs }))
              )
          );
          return forkJoin(requests);
        })
      )
      .subscribe({
        next: (results) => {
          if (!Array.isArray(results)) {
            this.professionalServicesMap.set({});
            return;
          }

          // Build professional -> services map
          const map: Record<string, Service[]> = {};
          for (const { service, professionals: profs } of results as any[]) {
            for (const prof of profs) {
              const profId = prof.userId || prof.professionalId;
              if (!map[profId]) map[profId] = [];
              map[profId].push(service);
            }
          }
          this.professionalServicesMap.set(map);
        },
        error: () => {
          this.professionalServicesMap.set({});
        },
      });
  }

  view(row: any) {
    console.log(row);
  }

  onCreateClient() {
    // Skip validation for system administrators
    if (this.authService.isSystemAdmin()) {
      this.openCreateModal();
      return;
    }

    // Validate professional limit before creating
    forkJoin({
      paidCount: this.membershipService.getPaidProfessionalsCount(),
      activeCount: of(this.activeProfessionalsCount), // Current count of active professionals
      status: this.membershipService.getMembershipStatus(),
    }).subscribe({
      next: ({ paidCount, activeCount, status }) => {
        // If active >= paid, user has reached the limit
        if (activeCount >= paidCount) {
          // Additional professionals cannot be bought during the trial: the plan has to be
          // purchased first, otherwise the add-on payment would replace the trial with a paid
          // membership the user never bought
          if (status.status === 'trial') {
            this.toastService.show(
              'Durante la prueba no puedes comprar profesionales adicionales. Contrata un plan primero.',
              'info',
              6000
            );
            this.router.navigate(['/settings/billing'], { queryParams: { view: 'plans' } });
            return;
          }

          this.purchaseProfessionals(
            1,
            'Completa el pago para agregar un profesional adicional'
          );
        } else {
          // User can create more professionals
          this.openCreateModal();
        }
      },
      error: (error) => {
        console.error('Error validating professional limit:', error);
        // On error, allow creation (fail open)
        this.openCreateModal();
      },
    });
  }

  /**
   * Open the ePayco checkout for additional professionals
   */
  private purchaseProfessionals(count: number, message: string) {
    this.professionalPaymentService.purchaseProfessionals(count).subscribe({
      next: () => {
        this.toastService.show(message, 'info', 5000);
      },
      error: (error) => {
        console.error('Error opening payment:', error);
        this.toastService.show(
          'Error al abrir el pago. Por favor, intenta nuevamente.',
          'error'
        );
      },
    });
  }

  /**
   * Open the create professional modal
   */
  private openCreateModal() {
    this.open = true;
    this.modalTitle = 'Nuevo profesional';
    this.modalInputs = {
      entityId: null,
      endpoint: this.endpoint,
    };
  }

  onEditClient(userId: any) {
    this.modalTitle = 'Editar profesional';
    this.modalInputs = {
      entityId: userId,
      endpoint: this.endpoint,
    };
    this.open = true;
  }

  openDelete(row: Professional) {
    this.toDeleteId = row.userId;
    this.toDeleteName = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || null;
    this.confirmOpen = true;
  }

  openToggleStatus(professional: Professional) {
    this.toToggleProfessional = professional;
    this.confirmToggleOpen = true;
  }

  onConfirmToggleStatus() {
    if (!this.toToggleProfessional) return;

    const professional = this.toToggleProfessional;
    const newStatus = !professional.isActive;

    this.preloader.show();
    this.businessService
      .toggleProfessionalStatus(this.businessId, professional.userId, newStatus)
      .subscribe({
        next: () => {
          this.toastService.show(
            `Profesional ${newStatus ? 'activado' : 'desactivado'} exitosamente`,
            'success'
          );
          this.load();
          this.loadProfessionalLimit();
          this.preloader.hide();
          this.confirmToggleOpen = false;
          this.toToggleProfessional = null;
        },
        error: (error) => {
          console.error('Error toggling professional status:', error);
          let errorMessage = 'Error al cambiar estado del profesional';

          // The API answers { code, errors: [...] }; business rules (no free seat, owner) come in errors
          if (error.status === 400 || error.error?.code === 'BUSINESS_RULE_VIOLATION') {
            errorMessage = error.error?.errors?.[0] || error.error?.message || 'No se puede activar: límite de profesionales alcanzado';
          }

          this.toastService.show(errorMessage, 'error');
          this.preloader.hide();
          this.confirmToggleOpen = false;
          this.toToggleProfessional = null;
        },
      });
  }

  onConfirmDelete() {
    if (!this.toDeleteId) return;
    this.deleteRecordById(this.toDeleteId);
  }

  protected override afterCreate(_res: any): void {
    if (_res) {
      this.toastService.show('Profesional guardado', 'success');
      this.load();
    }
  }

  protected override afterDelete(): void {
    this.toastService.show('Profesional eliminado', 'success');
    this.load();
  }

  protected override afterUpdate(): void {
    this.toastService.show('Cambios del profesional guardados', 'success');
    this.open = false;
    this.load();
  }

  override onModalConfirm(payload: Professional | any): void {
    if (!payload) {
      this.open = false;
      return;
    }

    const professionalId = payload.entityId;
    const avatarFile: File | null = payload.avatarFile || null;

    // Remove avatarFile from payload before sending to API
    const { avatarFile: _, ...professionalPayload } = payload;

    if (!professionalId) {
      // Create professional
      this.isSaving = true;
      this.businessService
        .createRecordByBusinessId<Professional>(this.businessId, professionalPayload, this.endpoint)
        .pipe(
          switchMap((res: any) => {
            const newProfessionalId = (res as any).userId || (res as any).id;
            const operations: any[] = [];

            // Upload avatar if provided
            if (newProfessionalId && avatarFile) {
              operations.push(
                this.businessService.uploadProfessionalAvatar(this.businessId, newProfessionalId, avatarFile)
              );
            }

            if (operations.length > 0) {
              return forkJoin(operations).pipe(
                switchMap(() => of(res))
              );
            }
            return of(res);
          })
        )
        .subscribe({
          next: (res) => {
            this.isSaving = false;
            this.open = false;
            // Reload professionals to show new professional with avatar
            this.load();
            this.afterCreate(res as Professional);
          },
          error: (err) => {
            this.isSaving = false;
            this.onCreateError(err);
          },
        });
    } else {
      // Update professional
      this.isSaving = true;
      this.businessService
        .updateRecordByBusinessId<Professional>(
          this.businessId,
          professionalId,
          professionalPayload,
          this.endpoint
        )
        .pipe(
          switchMap(() => {
            const operations: any[] = [];

            // Upload avatar if provided
            if (avatarFile) {
              operations.push(
                this.businessService.uploadProfessionalAvatar(this.businessId, professionalId, avatarFile)
              );
            }

            return operations.length > 0 ? forkJoin(operations) : of([]);
          })
        )
        .subscribe({
          next: () => {
            this.isSaving = false;
            // Reload professionals to show updated avatar
            this.load();
            this.afterUpdate();
          },
          error: (err) => {
            this.isSaving = false;
            this.onCreateError(err);
          },
        });
    }
  }
}
